import React from 'react';

export type ButtonGroupVariant = 'soft' | 'solid' | 'outlined' | 'plain';
export type ButtonGroupSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type ButtonGroupOrientation = 'horizontal' | 'vertical';

export interface ButtonGroupProps<T> {
  value: T;
  options: {
    value: T;
    label: string;
    disabled?: boolean;
  }[];
  variant?: ButtonGroupVariant;
  size?: ButtonGroupSize;
  orientation?: ButtonGroupOrientation;
  className?: string;
  name?: string;
  onChange?: (event: {
    e: React.ChangeEvent<HTMLInputElement>;
    value: T;
  }) => void;
}

const getButtonGroupClasses = (
  variant: ButtonGroupVariant = 'soft',
  orientation: ButtonGroupOrientation = 'horizontal'
) => {
  const baseClasses =
    'flex items-stretch gap-0 rounded-full w-full overflow-hidden';
  const variantClasses = {
    soft: 'bg-neutral-50',
    solid: 'bg-neutral-100',
    outlined: 'border border-neutral-200',
    plain: 'bg-transparent',
  };
  const orientationClasses = {
    horizontal: 'flex-row',
    vertical: 'flex-col',
  };

  return `${baseClasses} ${variantClasses[variant]} ${orientationClasses[orientation]}`;
};

const getButtonClasses = (
  variant: ButtonGroupVariant = 'soft',
  size: ButtonGroupSize = 'md',
  isChecked: boolean,
  orientation: ButtonGroupOrientation = 'horizontal'
) => {
  const baseClasses =
    'relative cursor-pointer transition-all duration-300 font-medium flex-1 min-w-0 flex items-center justify-center';

  const borderClasses =
    orientation === 'horizontal'
      ? 'border-r border-neutral-100 last:border-r-0'
      : 'border-b border-neutral-100 last:border-b-0';

  const variantClasses = {
    soft: [
      'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
      isChecked
        ? 'bg-neutral-200 text-neutral-900 font-semibold shadow-lg'
        : '',
    ].join(' '),
    solid: [
      'text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700',
      isChecked ? 'bg-neutral-700 text-white font-semibold shadow-lg' : '',
    ].join(' '),
    outlined: [
      'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700',
      isChecked
        ? 'bg-neutral-100 text-neutral-900 border-neutral-200 font-semibold shadow-lg'
        : '',
    ].join(' '),
    plain: [
      'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700',
      isChecked ? 'text-neutral-900 font-semibold shadow-lg' : '',
    ].join(' '),
  };
  const sizeClasses = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-9 px-3 text-xs',
    lg: 'h-10 px-4 text-xs',
    xl: 'h-11 px-5 text-xs',
    '2xl': 'h-12 px-6 text-xs',
  };

  return `${baseClasses} ${borderClasses} ${variantClasses[variant]} ${sizeClasses[size]}`;
};

const ButtonGroup = <T,>({
  value,
  options,
  variant = 'soft',
  size = 'md',
  orientation = 'horizontal',
  className = '',
  name = 'button-group',
  onChange,
}: ButtonGroupProps<T>): React.ReactElement => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = options.find(
      (option) => String(option.value) === event.target.value
    )?.value;
    onChange?.({ e: event, value: newValue });
  };

  return (
    <div
      role="radiogroup"
      aria-label={name}
      className={`${getButtonGroupClasses(variant, orientation)} ${className}`}
    >
      {options.map((option) => (
        <label
          data-testid={`button-group-${name}-${option.value}`}
          key={String(option.value)}
          className={`${getButtonClasses(
            variant,
            size,
            value === option.value,
            orientation
          )} ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={String(option.value)}
            checked={value === option.value}
            onChange={handleChange}
            disabled={option.disabled}
            className="sr-only"
          />
          <span className="block text-center px-0.5">{option.label}</span>
        </label>
      ))}
    </div>
  );
};

export default ButtonGroup;
